<template>

  <div class="portfolio-container">

    <!-- 🔘 Butoane categorii -->

    <div class="d-flex flex-wrap justify-content-center gap-2 mb-4">

      <button

        v-for="cat in categories"

        :key="cat.slug"

        class="btn btn-outline-primary"

        :class="{ active: selectedCategory === cat.slug }"

        @click="selectedCategory = selectedCategory === cat.slug ? null : cat.slug"

      >

        <i :class="`fas ${cat.icon}`"></i> {{ cat.name }}

      </button>

    </div>



    <!-- 🔍 Căutare -->

<input

  type="text"

  v-model="searchQuery"

  :placeholder="$t('portfolio.searchPlaceholder')"

  class="form-control custom-input mb-4"

/>



    <!-- 🔄 Loader -->

    <div v-if="loading" class="text-center my-5">

      <div class="spinner-border text-primary" role="status"></div>

    </div>



    <!-- ✅ Proiecte -->

    <div class="row g-4" v-if="!loading">

      <!-- ⚠️ Niciun proiect -->

      <div v-if="filteredProjects.length === 0" class="text-center text-primary col-12">

        <i class="fas fa-folder-open fa-2x mb-2 mt-3"></i>

<p>{{ $t('portfolio.noProjects') }}</p>

      </div>



      <!-- 🎯 Listă proiecte -->

<div class="col-md-6" v-for="project in paginatedProjects" :key="project.slug">

        <div class="card border-primary shadow-sm h-100">

          <img

            v-if="project.images.length"

            :src="getImageUrl(project.images[0].url)"

            class="card-img-top project-img"

            :alt="project.images[0].alt"

          />

          <div class="card-body">

            <h5 class="card-title">{{ project.title }}</h5>

            <div class="d-flex flex-wrap gap-1 mb-2">

  <span

    v-for="cat in project.categories"

    :key="cat"

    class="badge bg-primary text-dark text-uppercase"

  >

    {{ cat }}

  </span>

</div>



            <span class="text-white">{{ project.date_realizare }}</span>

            <p class="card-text">{{ project.description }}</p>



            <div class="d-flex flex-wrap gap-2 mb-2">

              <span

                v-for="tech in project.tech_stack"

                :key="tech.slug"

                class="badge bg-dark d-flex align-items-center gap-1"

              >

                <i :class="`fa ${tech.icon}`"></i> {{ tech.name }}

              </span>

            </div>



            <div class="mt-2 d-flex flex-wrap gap-2">

<router-link

  v-if="project.detalii"

  :to="`/portfolio/${project.slug}`"

  class="btn btn-sm btn-outline-primary"

>

  <i class="fas fa-link me-1"></i> {{ $t('portfolio.details') }}

</router-link>



<a

  v-if="project.source_code_url"

  :href="project.source_code_url"

  class="btn btn-sm btn-outline-primary"

  target="_blank"

>

  <i class="fas fa-code me-1"></i> {{ $t('portfolio.sourceCode') }}

</a>



<a

  v-if="project.live_url"

  :href="project.live_url"

  class="btn btn-sm btn-outline-primary"

  target="_blank"

>

  <i class="fas fa-globe me-1"></i> {{ $t('portfolio.live') }}

</a>



            </div>

          </div>

        </div>

      </div>

      <!-- ✅ PAGINARE FIXATĂ PENTRU MOBIL -->
      <div class="col-12" v-if="totalPages > 1">
        <div class="pagination-bar">

          <!-- ◀️ Prev -->
          <button
            class="btn btn-outline-primary pg-btn"
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            <i class="fas fa-angle-left"></i>
          </button>

          <!-- Prima pagină + ellipsis -->
          <template v-if="visiblePages[0] > 1">
            <button class="btn btn-outline-primary pg-btn" @click="currentPage = 1">1</button>
            <span class="pg-ellipsis" v-if="visiblePages[0] > 2">…</span>
          </template>

          <!-- Pagini vizibile (max 5 în jurul celei curente) -->
          <button
            v-for="page in visiblePages"
            :key="page"
            class="btn btn-outline-primary pg-btn"
            :class="{ active: currentPage === page }"
            @click="currentPage = page"
          >
            {{ page }}
          </button>

          <!-- Ultima pagină + ellipsis -->
          <template v-if="visiblePages[visiblePages.length - 1] < totalPages">
            <span class="pg-ellipsis" v-if="visiblePages[visiblePages.length - 1] < totalPages - 1">…</span>
            <button class="btn btn-outline-primary pg-btn" @click="currentPage = totalPages">{{ totalPages }}</button>
          </template>

          <!-- ▶️ Next -->
          <button
            class="btn btn-outline-primary pg-btn"
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            <i class="fas fa-angle-right"></i>
          </button>

        </div>
      </div>

    </div>

  </div>

</template>



<script setup>

import { ref, computed, onMounted, watch } from 'vue'

import { useI18n } from 'vue-i18n'

import axios from 'axios'



const { locale } = useI18n()



const selectedCategory = ref(null)

const searchQuery = ref("")

const allProjects = ref([])

const categories = ref([])

const currentPage = ref(1)

const itemsPerPage = 4

const loading = ref(true)



const filteredProjects = computed(() => {

  return allProjects.value.filter(p => {

    const matchesCategory = !selectedCategory.value || p.categories.includes(selectedCategory.value)

    const matchesQuery =

      p.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||

      p.tech_stack.some(t => t.name.toLowerCase().includes(searchQuery.value.toLowerCase()))

    return matchesCategory && matchesQuery

  })

})



const paginatedProjects = computed(() => {

  const start = (currentPage.value - 1) * itemsPerPage

  return filteredProjects.value.slice(start, start + itemsPerPage)

})



const totalPages = computed(() =>

  Math.ceil(filteredProjects.value.length / itemsPerPage)

)



// Afișează max 5 butoane de pagină în jurul celei curente — fix mobil
const visiblePages = computed(() => {

  const total = totalPages.value

  const cur = currentPage.value

  const delta = 2

  const pages = []

  for (let i = Math.max(1, cur - delta); i <= Math.min(total, cur + delta); i++) {

    pages.push(i)

  }

  return pages

})



watch([selectedCategory, searchQuery, locale], () => {

  currentPage.value = 1

  fetchProjects()

})



const getImageUrl = (url) => {

  if (!url) return ''

  if (url.startsWith('http')) return url

  return `https://api.rusin.ro${url}`

}



const fetchProjects = async () => {

  const lang = locale.value

  loading.value = true

  try {

    const res = await axios.get(`https://api.rusin.ro/portofolio/?lang=${lang}`)

    categories.value = res.data.categories

    allProjects.value = res.data.projects

  } catch (err) {

    console.error("❌ Axios CORS/Network error:", err)

  } finally {

    loading.value = false

  }

}



onMounted(fetchProjects)



watch(locale, () => {

  fetchProjects()

})



</script>



<style scoped>

.project-box {

  background-color: #111;

  border: 1px solid #00ffcc44;

  transition: all 0.2s ease;

}

.project-box:hover {

  transform: scale(1.03);

  border-color: #00ffcc;

  box-shadow: 0 0 10px #00ffcc33;

}

input.custom-input,

textarea.custom-input {

  color: #fff !important;

}



.custom-input {

  background-color: #222;

  border: 1px solid #00ffcc;

  color: white;

  

  font-family: 'Fira Code', monospace;

  transition: all 0.3s ease;

  box-shadow: 0 0 5px transparent;

}

.custom-input::placeholder {

  color: #00ffccaa;

}

.custom-input:input{

  color: white;

}

.custom-input:focus {

  border-color: #00ffcc;

  box-shadow: 0 0 8px #00ffccaa;

  background-color: #111;

}



.text-primary {

  color: #00ffcc !important;

}

.project-img {

  width: 100%;

  height: 220px;

  object-fit: fill;

  border-bottom: 2px solid #00ffcc;

  margin: 0;

  

  padding: 0;

  display: block;

}



.bg-primary {

  background-color: #00ffcc !important;

}

.border-primary {

  border-color: #00ffcc !important;

}

.card {

  background-color: #151515;

  border-radius: 10px;

    overflow: hidden;



  transition: transform 0.3s ease;

}

.card:hover {

  transform: scale(1.02);

  box-shadow: 0 0 12px #00ffcc44;

}

.card-title {

  font-weight: bold;

  color: #00ffcc;

  font-family: 'Fira Code', monospace;

}

.card-text {

  font-size: 0.9rem;

  color: #ccc;

}

.btn-outline-primary {

  position: relative;

  color: #00ffcc !important;

  border: 2px solid #00ffcc;

  background-color: transparent;

  transition: all 0.3s ease;

  font-weight: 600;

  letter-spacing: 0.5px;

  box-shadow: 0 0 8px transparent;

}



.btn-outline-primary:hover {

  color: #fff !important;

  background-color: #00ffcc !important;

  border-color: #00ffcc;

  transform: translateY(-2px);

}

.btn-outline-primary.active {

  color: #111 !important;

  background-color: #00ffcc !important;

  border-color: #00ffcc;

  transform: translateY(-2px);

}

.btn-outline-primary:disabled {

  color: #fff !important;

  border-color: #00a584;

  transform: translateY(-2px);

}

/* ── PAGINARE MOBILĂ ── */
.pagination-bar {

  display: flex;

  flex-wrap: wrap;

  justify-content: center;

  align-items: center;

  gap: 6px;

  margin-top: 1rem;

}

.pg-btn {

  min-width: 38px;

  padding: 6px 10px;

  font-size: 0.875rem;

}

.pg-ellipsis {

  color: #00ffcc88;

  font-size: 1rem;

  padding: 0 2px;

  align-self: center;

  user-select: none;

}

@media (max-width: 480px) {

  .pg-btn {

    min-width: 32px;

    padding: 4px 8px;

    font-size: 0.8rem;

  }

}

</style>